import { assignInlineVars } from "@vanilla-extract/dynamic";
import { clsx } from "clsx";
import React, { useEffect, useState } from "react";
import { Divider } from "web/react/components/divider";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Heading } from "web/react/emo/heading";
import { Modal } from "web/react/emo/modal";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useWindowHeight } from "web/react/hooks/use-window-height/use-window-height";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import { PriceComparisonCards } from "web/react/pages/product/in-stock/in-stock-product-page/product-price-comparison/price-comparison-cards";
import { PriceComparisonTable } from "web/react/pages/product/in-stock/in-stock-product-page/product-price-comparison/price-comparison-table";
import {
    getPriceRangeInfoPrices,
    isSelectedSize,
} from "web/react/pages/product/in-stock/in-stock-product-page/utils";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./product-price-comparison.css";

interface ComparePriceLinkProps {
    count: number;
    onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

export function ComparePriceLink({ count, onClick }: ComparePriceLinkProps): JSX.Element {
    return (
        <button className={styles.comparePricesButton}>
            <a data-testid="compare-prices-button" className={styles.flex} onClick={onClick}>
                <Text as="span" textStyle={"body-bold"} className={styles.comparePriceCTA}>
                    {gettext("product.buy_area.compare_count_prices.cta", {
                        count,
                    })}
                </Text>
                <SVGIcon
                    name="arrow-down"
                    className={clsx("hidden-mobile", styles.compareSVGIcon)}
                />
            </a>
        </button>
    );
}

export function ProductPriceComparison(): JSX.Element | null {
    const { isMobileViewport } = useDomViewport();

    const {
        product,
        buyOptions,
        termsUrl,
        comparison: {
            isComparePriceOpen,
            closeCompareModal,
            displayedBuyOptions,
            setDisplayedBuyOptions,
        },
        activeProduct: { selectedSizeOption },
        comparePricesRef,
        productPurchaseType,
    } = useInStockProductPageContext();

    const isCheckout = productPurchaseType === ProductPurchaseType.CHECKOUT;

    const [minBuyOptionPrice, setMinBuyOptionPrice] = useState<string | null>(null);
    const [maxBuyOptionPrice, setMaxBuyOptionPrice] = useState<string | null>(null);

    const pageHeight = useWindowHeight();

    useEffect(() => {
        // When there is a size picker
        if (selectedSizeOption) {
            const filteredBuyOptions = buyOptions.filter((buyOption) =>
                buyOption.sizes.some((size) => isSelectedSize(selectedSizeOption, size))
            );
            setDisplayedBuyOptions(filteredBuyOptions);
        }
    }, [selectedSizeOption, buyOptions, setDisplayedBuyOptions]);

    useEffect(() => {
        if (isCheckout) {
            const { minPrice, maxPrice } = getPriceRangeInfoPrices(displayedBuyOptions);

            setMinBuyOptionPrice(minPrice);
            setMaxBuyOptionPrice(maxPrice);
        }
    }, [isCheckout, displayedBuyOptions]);

    if (displayedBuyOptions.length <= 1) {
        return null;
    }

    if (isCheckout) {
        const title = (
            <Heading as={"h2"} textStyle={"subhead"}>
                {gettext("price_comparison.compare_prices")}
            </Heading>
        );

        const pricesBetween = minBuyOptionPrice && maxBuyOptionPrice && (
            <div className={styles.pricesBetween}>
                <SVGIcon name="price-change" />
                <Text as="p" textStyle={"body-s"}>
                    {gettext("price_comparison.prices_between", {
                        min_price: minBuyOptionPrice,
                        max_price: maxBuyOptionPrice,
                    })}
                </Text>
            </div>
        );

        const priceComparisonCards = (
            <PriceComparisonCards product={product} buyOptions={displayedBuyOptions} />
        );

        if (isMobileViewport) {
            return (
                <Modal
                    isOpen={isComparePriceOpen}
                    onAfterOpen={() => {
                        analytics.event({
                            category: "compare-prices",
                            action: "shown",
                            label: "checkout",
                        });
                    }}
                    onClose={() => {
                        closeCompareModal();
                        analytics.event({
                            category: "compare-prices",
                            action: "modal_dismissed",
                            label: "checkout",
                        });
                    }}
                    className={styles.comparisonModal}
                >
                    <div
                        style={assignInlineVars({
                            [styles.pageHeight]: pageHeight ? `${pageHeight}px` : "100vh",
                        })}
                        className={styles.mobileWrapper}
                    >
                        <div className={styles.mobileHeader}>
                            <div className={styles.mobileTitle}>{title}</div>
                            <Divider />
                            <View paddingY="xs">{pricesBetween}</View>
                        </div>
                        {priceComparisonCards}
                    </div>
                </Modal>
            );
        } else {
            return (
                <div ref={comparePricesRef} data-testid="price-comparison-redesign">
                    <div className={styles.desktopTitle}>
                        {title}
                        {pricesBetween}
                    </div>
                    {priceComparisonCards}
                </div>
            );
        }
    }

    const priceComparisonArea = (
        <VStack spacing="md">
            <Text as="span" textStyle={"body-1"} color={"secondary"}>
                {gettext("in_stock_product.compare_prices.compare_prices_desc")}
            </Text>
            <PriceComparisonTable buyOptions={displayedBuyOptions} termsUrl={termsUrl} />
        </VStack>
    );

    return (
        <>
            <section
                ref={comparePricesRef}
                className="hidden-mobile"
                data-testid="price-comparison"
            >
                <View>
                    <Heading as={"h2"} textStyle={"title-2"} className={styles.title}>
                        {gettext("in_stock_product.compare_prices.compare_prices_title")}
                    </Heading>
                    {priceComparisonArea}
                </View>
            </section>
            <Modal
                isOpen={isComparePriceOpen}
                onAfterOpen={() => {
                    analytics.event({
                        category: "compare-prices",
                        action: "shown",
                        label: "pdp",
                    });
                }}
                onClose={() => {
                    closeCompareModal();
                    analytics.event({
                        category: "compare-prices",
                        action: "modal_dismissed",
                        label: "pdp",
                    });
                }}
                className={clsx("hidden-desktop", "hidden-tablet-only", styles.comparisonModal)}
                closeClassName={styles.closeButton}
            >
                <div
                    style={assignInlineVars({
                        [styles.pageHeight]: pageHeight ? `${pageHeight}px` : "100vh",
                    })}
                    className={styles.mobileWrapper}
                >
                    <View paddingX="sm">
                        <Heading as={"h2"} textStyle={"title-2"} className={styles.title}>
                            {gettext("in_stock_product.compare_prices.compare_prices_title")}
                        </Heading>
                    </View>
                    <div className={styles.mobileTable}>{priceComparisonArea}</div>
                </div>
            </Modal>
        </>
    );
}
